import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Card, Modal, Form, InputNumber, Select, message, Tooltip, Tag } from 'antd';
import { PlusOutlined, ReloadOutlined, ProfileOutlined, BankOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { payrollApi, employeesApi, bankAccountsApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa } from '../../utils/formatPriceToman';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const Payroll = () => {
  const [loading, setLoading] = useState(false);
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPayModalVisible, setIsPayModalVisible] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [form] = Form.useForm();
  const [payForm] = Form.useForm();

  const fetchDependencies = async () => {
    try {
      const [empRes, bankRes] = await Promise.all([
        employeesApi.getAll(),
        bankAccountsApi.getAll(),
      ]);
      if (empRes?.data) setEmployees(empRes.data);
      if (bankRes?.data?.accounts) setBankAccounts(bankRes.data.accounts);
      else if (bankRes?.accounts) setBankAccounts(bankRes.accounts);
    } catch (error) {
      console.error('Error fetching dependencies:', error);
    }
  };

  const fetchPayrolls = async () => {
    // In a real app, there would be a get all endpoint for payroll, but since it's not documented, we mock it or expect the backend to have one if needed.
    // Assuming we have one for viewing.
    setLoading(true);
    try {
      // Mocked getting payrolls or calling a hypothetical endpoint
      // const res = await payrollApi.getAll();
      // setPayrolls(res.data);
      setPayrolls([]);
    } catch (error) {
      console.error('Error fetching payrolls:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDependencies();
    fetchPayrolls();
  }, []);

  const handleGenerate = async (values) => {
    setActionLoading(true);
    try {
      await payrollApi.generate({
        ...values,
        period_year: Number(values.period_year),
        period_month: Number(values.period_month),
        base_salary: Number(values.base_salary),
        overtime_amount: Number(values.overtime_amount || 0),
        bonus_amount: Number(values.bonus_amount || 0),
        deductions_amount: Number(values.deductions_amount || 0),
      });
      message.success('فیش حقوقی با موفقیت صادر شد');
      setIsModalVisible(false);
      form.resetFields();
      fetchPayrolls();
    } catch (error) {
      message.error('خطا در صدور فیش حقوقی');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePay = async (values) => {
    if (!selectedPayroll) return;
    setActionLoading(true);
    try {
      await payrollApi.pay(selectedPayroll.id, {
        bank_account_id: values.bank_account_id,
      });
      message.success('فیش حقوقی پرداخت شد');
      setIsPayModalVisible(false);
      payForm.resetFields();
      fetchPayrolls();
    } catch (error) {
      message.error('خطا در پرداخت فیش حقوقی');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>کارمند</span>,
      key: 'user',
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {record.user?.name || '-'}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>دوره</span>,
      key: 'period',
      render: (_, record) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {toPersianNumber(record.period_year)}/{toPersianNumber(record.period_month)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مبلغ قابل پرداخت</span>,
      dataIndex: 'net_payable',
      key: 'net_payable',
      render: (amount) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(amount)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'paid' ? 'success' : 'warning'} style={{ fontFamily: 'CustomFont' }}>
          {status === 'paid' ? 'پرداخت شده' : 'در انتظار پرداخت'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          {record.status !== 'paid' && (
            <Button
              type="primary"
              size="small"
              icon={<BankOutlined />}
              onClick={() => {
                setSelectedPayroll(record);
                setIsPayModalVisible(true);
              }}
              style={{ fontFamily: 'CustomFont', backgroundColor: '#52c41a' }}
            >
              پرداخت
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center">
                  <ProfileOutlined className="text-teal-600 dark:text-teal-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                    فیش حقوقی
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    صدور و پرداخت فیش حقوقی پرسنل
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip title="بروزرسانی">
                  <Button icon={<ReloadOutlined />} onClick={fetchPayrolls} size="middle" loading={loading} />
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsModalVisible(true)}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  صدور فیش جدید
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={4} />
            ) : (
              <Card className="dark:bg-gray-800 border-0 shadow-sm">
                <Table
                  columns={columns}
                  dataSource={payrolls}
                  rowKey="id"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>صدور فیش حقوقی</span>}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleGenerate}>
          <Form.Item name="user_id" label={<span style={{ fontFamily: 'CustomFont' }}>کارمند</span>} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" style={{ fontFamily: 'CustomFont' }}>
              {employees.map(e => <Option key={e.id} value={e.id}>{e.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="period_year" label={<span style={{ fontFamily: 'CustomFont' }}>سال</span>} rules={[{ required: true }]}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} defaultValue={new Date().getFullYear()} />
          </Form.Item>
          <Form.Item name="period_month" label={<span style={{ fontFamily: 'CustomFont' }}>ماه</span>} rules={[{ required: true }]}>
            <InputNumber min={1} max={12} className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="base_salary" label={<span style={{ fontFamily: 'CustomFont' }}>حقوق پایه</span>} rules={[{ required: true }]}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="overtime_amount" label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ اضافه‌کاری</span>}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="bonus_amount" label={<span style={{ fontFamily: 'CustomFont' }}>سایر پاداش‌ها</span>}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="deductions_amount" label={<span style={{ fontFamily: 'CustomFont' }}>کسورات</span>}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          
          <div className="mb-4 text-xs text-gray-500" style={{ fontFamily: 'CustomFont' }}>
            توجه: موتور خودکار مجموع پورسانت‌های تایید شده این ماه را به فیش اضافه کرده و مبلغ نهایی (net_payable) را محاسبه خواهد کرد.
          </div>

          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                صدور فیش
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>پرداخت فیش حقوقی</span>}
        open={isPayModalVisible}
        onCancel={() => setIsPayModalVisible(false)}
        footer={null}
      >
        <Form form={payForm} layout="vertical" onFinish={handlePay}>
          <Form.Item name="bank_account_id" label={<span style={{ fontFamily: 'CustomFont' }}>حساب بانکی</span>} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" style={{ fontFamily: 'CustomFont' }}>
              {bankAccounts.map(b => <Option key={b.id} value={b.id}>{b.account_name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsPayModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#52c41a' }}>
                ثبت پرداخت
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default Payroll;
